import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ProvedorConexao, TipoCredencial, TipoProvedor } from '../../../types/smartinteg';

export const CAMPOS_CONEXAO = [
  'nomeConexao', 'ambiente', 'padrao', 'ativo',
  'urlBase', 'timeout', 'retry_tentativas', 'retry_intervalo',
  'tipo_bd', 'host', 'porta', 'database', 'driverClass',
  'alterarCredencial', 'tipoCredencial', 'cred_user', 'cred_password', 'cred_token'
];

// Só Oracle monta connection string no backend; os demais falham na execução
export const TIPOS_BANCO_DADOS = [
  { label: 'Oracle', value: 'oracle', disabled: false },
  { label: 'PostgreSQL (não suportado)', value: 'postgresql', disabled: true },
  { label: 'MySQL (não suportado)', value: 'mysql', disabled: true },
  { label: 'SQL Server (não suportado)', value: 'sqlserver', disabled: true }
];

// O runtime só aplica BEARER (Authorization: Bearer) e DATABASE; BASIC/API_KEY/OAUTH2 são gravados e ignorados
const CREDENCIAIS_API = [
  { label: 'Sem credencial', value: TipoCredencial.SEM_CREDENCIAL },
  { label: 'Bearer Token (estático)', value: TipoCredencial.BEARER }
];
const CREDENCIAIS_BANCO = [
  { label: 'Usuário e senha do banco', value: TipoCredencial.DATABASE }
];

export function opcoesCredencial(tipo: TipoProvedor) {
  return tipo === TipoProvedor.APIREST ? CREDENCIAIS_API : CREDENCIAIS_BANCO;
}

export function credencialPadrao(tipo: TipoProvedor): TipoCredencial {
  return tipo === TipoProvedor.APIREST ? TipoCredencial.SEM_CREDENCIAL : TipoCredencial.DATABASE;
}

export function rotuloCredencial(tipo: TipoCredencial | null | undefined): string {
  switch (tipo) {
    case TipoCredencial.SEM_CREDENCIAL: return 'Sem credencial';
    case TipoCredencial.BEARER: return 'Bearer Token';
    case TipoCredencial.DATABASE: return 'Usuário e senha do banco';
    case TipoCredencial.BASIC: return 'Basic (não aplicado)';
    case TipoCredencial.API_KEY: return 'API Key (não aplicado)';
    case TipoCredencial.OAUTH2: return 'OAuth2 (não aplicado)';
    default: return 'Nenhuma gravada';
  }
}

export function valoresPadraoConexao(tipo: TipoProvedor) {
  return {
    nomeConexao: '', ambiente: '', padrao: false, ativo: true,
    urlBase: '', timeout: 30000, retry_tentativas: 3, retry_intervalo: 1000,
    tipo_bd: 'oracle', host: '', porta: null as number | null, database: '', driverClass: '',
    alterarCredencial: false,
    tipoCredencial: credencialPadrao(tipo), cred_user: '', cred_password: '', cred_token: ''
  };
}

export function controlesConexao(): Record<string, any[]> {
  const v = valoresPadraoConexao(TipoProvedor.BASEDADOS);
  return {
    nomeConexao: [v.nomeConexao, [Validators.required, Validators.minLength(3)]],
    ambiente: [v.ambiente, Validators.required],
    padrao: [v.padrao],
    ativo: [v.ativo],
    urlBase: [v.urlBase],
    timeout: [v.timeout],
    retry_tentativas: [v.retry_tentativas],
    retry_intervalo: [v.retry_intervalo],
    tipo_bd: [v.tipo_bd],
    host: [v.host],
    porta: [v.porta],
    database: [v.database],
    driverClass: [v.driverClass],
    alterarCredencial: [v.alterarCredencial],
    tipoCredencial: [v.tipoCredencial, Validators.required],
    cred_user: [v.cred_user],
    cred_password: [v.cred_password],
    cred_token: [v.cred_token]
  };
}

// Na edição a credencial só é enviada (e validada) se o usuário optar por alterá-la
export function editandoCredencial(form: FormGroup, modoEdicao: boolean): boolean {
  return !modoEdicao || !!form.get('alterarCredencial')?.value;
}

export function aplicarValidadoresConexao(form: FormGroup, tipo: TipoProvedor, modoEdicao: boolean = false): void {
  const api = tipo === TipoProvedor.APIREST;
  const credencial = form.get('tipoCredencial')?.value as TipoCredencial;
  const validaCredencial = editandoCredencial(form, modoEdicao);
  const exigeCredencial = (alvo: TipoCredencial) => validaCredencial && credencial === alvo;

  const regras: Record<string, ValidatorFn[]> = {
    // timeout é obrigatório: sem ele o runtime lança NPE
    urlBase: api ? [Validators.required, Validators.pattern(/^https?:\/\/\S+$/)] : [],
    timeout: api ? [Validators.required, Validators.min(1000)] : [],
    retry_tentativas: api ? [Validators.required, Validators.min(0)] : [],
    retry_intervalo: api ? [Validators.required, Validators.min(100)] : [],

    tipo_bd: api ? [] : [Validators.required],
    host: api ? [] : [Validators.required],
    porta: api ? [] : [Validators.required, Validators.min(1), Validators.max(65535)],
    database: api ? [] : [Validators.required],

    tipoCredencial: validaCredencial ? [Validators.required] : [],
    cred_user: exigeCredencial(TipoCredencial.DATABASE) ? [Validators.required] : [],
    cred_password: exigeCredencial(TipoCredencial.DATABASE) ? [Validators.required] : [],
    cred_token: exigeCredencial(TipoCredencial.BEARER) ? [Validators.required] : []
  };

  Object.entries(regras).forEach(([campo, validadores]) => {
    const control = form.get(campo);
    control?.setValidators(validadores);
    control?.updateValueAndValidity({ emitEvent: false });
  });
}

// O PUT do backend substitui o config inteiro: parte do config existente para preservar chaves que a tela não edita
export function montarConfig(v: any, tipo: TipoProvedor, base: Record<string, any> = {}): Record<string, any> {
  const config: Record<string, any> = { ...base };

  if (tipo === TipoProvedor.APIREST) {
    config['url_base'] = v.urlBase;
    config['timeout'] = v.timeout;
    if (v.retry_tentativas > 0) {
      config['retry'] = { ...(base['retry'] ?? {}), tentativas: v.retry_tentativas, intervalo: v.retry_intervalo };
    } else {
      delete config['retry'];
    }
  } else {
    config['tipo_bd'] = v.tipo_bd;
    config['host'] = v.host;
    config['porta'] = v.porta;
    config['database'] = v.database;
    if (v.driverClass) {
      config['driverClass'] = v.driverClass;
    } else {
      delete config['driverClass'];
    }
  }

  return config;
}

export function montarCredenciais(v: any): { user?: string; password?: string; token?: string } {
  switch (v.tipoCredencial) {
    case TipoCredencial.DATABASE: return { user: v.cred_user, password: v.cred_password };
    case TipoCredencial.BEARER: return { token: v.cred_token };
    default: return {};
  }
}

export function valoresDaConexao(c: ProvedorConexao) {
  const cfg = c.config ?? {};
  return {
    nomeConexao: c.nome,
    ambiente: c.ambiente,
    padrao: c.padrao,
    ativo: c.ativo,
    urlBase: cfg.url_base ?? c.urlBase ?? '',
    timeout: cfg.timeout ?? 30000,
    retry_tentativas: cfg.retry?.tentativas ?? 0,
    retry_intervalo: cfg.retry?.intervalo ?? 1000,
    tipo_bd: String(cfg.tipo_bd ?? 'oracle').toLowerCase(),
    host: cfg.host ?? '',
    porta: cfg.porta ?? null,
    database: cfg.database ?? '',
    driverClass: cfg.driverClass ?? ''
  };
}
